import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FacadeImage } from '@/ui/components/molecules/FacadeImage';
import { getStatusColor } from '@/ui/components/molecules/ExpertiseCard/utils/tagRules';
import { TrackerChannel } from '../../types/tracker.types';
import { RuleEvaluation } from '../../utils/statusGroupRules';
import './TrackerChannelCard.styles.css';
import '@/ui/components/molecules/ExpertiseCard/ExpertiseCard.styles.css';

export interface TrackerChannelCardProps {
  channel: TrackerChannel;
  currentUserId?: string; // Current user ID to check if user is responsable
  onFileMaker?: () => void;
  onSubscribe?: () => void;
  onUnsubscribe?: () => void;
  onUrgencyClick?: () => void; // Handler to open urgency detail modal
  isSubscribing?: boolean;
  ruleEvaluations?: RuleEvaluation[]; // Status group rule evaluations
  hasNotification?: boolean; // Whether this affaire has unread content
  onClearNotification?: () => void; // Handler to clear notification (mark all as read)
  isClearingNotification?: boolean; // Whether clearing is in progress
  onMarkUnread?: () => void; // Handler to mark affaire as unread
  isMarkingUnread?: boolean; // Whether marking unread is in progress
  isHighlighted?: boolean; // Whether this card should be highlighted (just returned from detail)
}

/**
 * TrackerChannelCard - Custom card specifically designed for tracker channels
 * Displays channel information with subscription status and communication counts
 */
export const TrackerChannelCard: React.FC<TrackerChannelCardProps> = ({
  channel,
  currentUserId: _currentUserId,
  onFileMaker: _onFileMaker,
  onSubscribe: _onSubscribe,
  onUnsubscribe: _onUnsubscribe,
  onUrgencyClick,
  isSubscribing: _isSubscribing = false,
  ruleEvaluations = [],
  hasNotification = false,
  onClearNotification,
  isClearingNotification = false,
  onMarkUnread,
  isMarkingUnread = false,
  isHighlighted = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
    navigate(`${basePath}/tracker/${channel.affaire_id}`, {
      state: {
        from: `${basePath}/tracker`,
        lastClickedAffaireId: channel.affaire_id
      }
    });
  };

  const handleUrgencyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUrgencyClick) {
      onUrgencyClick();
    }
  };

  // Get priority color helper
  const getPriorityColor = (priority: 'none' | 'low' | 'medium' | 'high' | 'urgent') => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#6b7280';
      case 'none': return '#e5e7eb';
      default: return '#e5e7eb';
    }
  };

  // Get priority emoji
  const getPriorityEmoji = (priority: 'none' | 'low' | 'medium' | 'high' | 'urgent') => {
    switch (priority) {
      case 'urgent': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🔵';
      case 'low': return '⚪';
      case 'none': return '➖';
      default: return '➖';
    }
  };

  // Get priority label
  const getPriorityLabel = (priority: 'none' | 'low' | 'medium' | 'high' | 'urgent') => {
    switch (priority) {
      case 'urgent': return 'Urgent';
      case 'high': return 'Haute';
      case 'medium': return 'Moyenne';
      case 'low': return 'Basse';
      case 'none': return 'Aucune';
      default: return 'Aucune';
    }
  };

  // Build address display
  const addressParts = [
    channel.numero_rue,
    channel.adresse_rue_bien,
    channel.commune_bien
  ].filter(Boolean);

  const fullAddress = addressParts.length > 0 ? addressParts.join(' ') : 'Adresse non renseignée';
  const address = fullAddress.length > 70 ? fullAddress.substring(0, 67) + '...' : fullAddress;

  // Format date with time
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('fr-BE', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${dateStr} ${timeStr}`;
  };

  // Get highest severity rule (for card border/indicator)
  const highestSeverity = ruleEvaluations.length > 0
    ? ruleEvaluations.reduce((highest, current) => {
        if (current.severity === 'critical') return 'critical';
        if (current.severity === 'warning' && highest !== 'critical') return 'warning';
        return highest;
      }, 'info' as 'info' | 'warning' | 'critical')
    : null;

  // Build className with severity and highlight states
  const cardClassName = [
    'tracker-channel-card',
    highestSeverity && `tracker-channel-card--${highestSeverity}`,
    isHighlighted && 'tracker-channel-card--highlighted'
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClassName}
      onClick={handleClick}
      data-affaire-id={channel.affaire_id}
    >
      {/* Facade Image - Full Height */}
      <div className="tracker-channel-card__image">
        <FacadeImage
          affaireId={channel.affaire_id.toString()}
          className="tracker-channel-card__facade"
        />
      </div>

      {/* Main Content */}
      <div className="tracker-channel-card__content">
        {/* Pipeline Status Badge - Top right corner */}
        {channel.status && (
          <div className="tracker-channel-card__status-corner">
            <span className={`status-badge status-${getStatusColor(channel.status)}`}>
              {channel.status}
            </span>
          </div>
        )}

        {/* Header with ID, responsable name, and urgency badge */}
        <div className="tracker-channel-card__header">
          <div className="tracker-channel-card__header-left">
            <span className="tracker-channel-card__id">#{channel.affaire_id}</span>
            {/* Responsable Name - in header where status was */}
            <span className="tracker-channel-card__responsable-inline">
              {channel.responsable_name || 'Non assigné'}
            </span>
            {/* Urgency Badge - only show if priority is not 'none' and todo exists */}
            {channel.todo && !channel.todo.is_done && channel.todo.priority !== 'none' && (
              <span
                className="tracker-urgency-badge"
                style={{
                  background: getPriorityColor(channel.todo.priority),
                  color: channel.todo.priority === 'low' ? '#374151' : 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'opacity 0.2s'
                }}
                onClick={handleUrgencyClick}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                title="Cliquez pour voir les détails"
              >
                <span>{getPriorityEmoji(channel.todo.priority)}</span>
                <span>{getPriorityLabel(channel.todo.priority)}</span>
              </span>
            )}
          </div>

        </div>

        {/* Channel Name */}
        <h3 className="tracker-channel-card__title">{channel.name}</h3>

        {/* Address */}
        <p className="tracker-channel-card__address">{address}</p>

        {/* Date - after address */}
        <div className="tracker-channel-card__date">
          {formatDate(channel.last_activity_date || channel.created_at)}
        </div>

        {/* Footer with rules */}
        {ruleEvaluations.length > 0 && (
          <div className="tracker-channel-card__footer">
            <div className="tracker-channel-card__rules">
              {ruleEvaluations.map((evaluation, index) => (
                <div
                  key={`${evaluation.ruleId}-${index}`}
                  className={`tracker-rule-badge tracker-rule-badge--${evaluation.severity}`}
                  title={evaluation.suggestedAction || evaluation.message}
                >
                  <span className="tracker-rule-badge__icon">
                    {evaluation.severity === 'critical' ? '⚠️' : evaluation.severity === 'warning' ? '⚡' : 'ℹ️'}
                  </span>
                  <span className="tracker-rule-badge__text">{evaluation.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Notification indicator - Right side */}
      {hasNotification && (
        <div className="tracker-channel-card__notification" onClick={(e) => e.stopPropagation()}>
          <div className="tracker-channel-card__notification-indicator">
            <span className="tracker-channel-card__notification-label">Non lu</span>
          </div>
          {onClearNotification && (
            <button
              className="tracker-channel-card__notification-clear"
              onClick={(e) => {
                e.stopPropagation();
                onClearNotification();
              }}
              disabled={isClearingNotification}
              title="Marquer comme lu"
            >
              {isClearingNotification ? (
                <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          )}
        </div>
      )}

      {/* Mark as unread button - shown when no notification */}
      {!hasNotification && onMarkUnread && (
        <div className="tracker-channel-card__mark-unread" onClick={(e) => e.stopPropagation()}>
          <button
            className="tracker-channel-card__mark-unread-btn"
            onClick={(e) => {
              e.stopPropagation();
              onMarkUnread();
            }}
            disabled={isMarkingUnread}
            title="Marquer comme non lu"
          >
            {isMarkingUnread ? (
              <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
